// SEO helpers used by the <SEO> component on every page.
// Keep keys explicit — search engines and previews are unforgiving to typos.

import { ROUTES } from '@/data/routes';

const SITE_URL = 'https://insidepanama.com';
// PNG (not SVG): Facebook, LinkedIn, WhatsApp and iMessage refuse to
// render SVG Open Graph images. The PNG is generated at build time
// from public/og-default.svg by scripts/build-og.cjs.
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-default.png`;

export const OG_LOCALE_BY_LANG = {
  fr: 'fr_FR',
  en: 'en_US',
  es: 'es_PA',
};

// Strip any /en or /es prefix from a path so we can build canonical/alternate
// URLs from the language-neutral remainder.
function stripLangPrefix(pathname) {
  return pathname.replace(/^\/(en|es)(?=\/|$)/, '') || '/';
}

export function buildCanonical(pathname, lang = 'fr') {
  const clean = stripLangPrefix(pathname);
  const tail = clean === '/' ? '' : clean;
  return lang === 'fr' ? `${SITE_URL}${tail || '/'}` : `${SITE_URL}/${lang}${tail}`;
}

/**
 * Table inverse chemin → clé de route, tous langues confondues.
 *
 * Construite une fois, à la demande : ROUTES importe categories.js, et un
 * import au chargement du module créerait un cycle avec les consommateurs
 * de seo.js.
 */
let PATH_TO_KEY = null;
function pathToKey(path) {
  if (!PATH_TO_KEY) {
    PATH_TO_KEY = new Map();
    for (const [key, byLang] of Object.entries(ROUTES)) {
      if (!byLang || typeof byLang !== 'object') continue;
      for (const p of Object.values(byLang)) {
        if (typeof p === 'string') PATH_TO_KEY.set(p, key);
      }
    }
  }
  return PATH_TO_KEY.get(path) ?? null;
}

/**
 * Alternates hreflang d'une page.
 *
 * ⚠️ Les segments de route sont TRADUITS sur ce site (`/questions` ·
 * `/en/questions` · `/es/preguntas`). L'ancienne version préfixait simplement
 * la langue au chemin courant, ce qui annonçait `/es/questions` — une URL qui
 * n'existe pas. Le défaut est resté invisible tant que <SEOHead> n'émettait
 * rien ; il pointait vers des 404, ce que Google pénalise (le commentaire de
 * SEOHead sur les articles le disait déjà pour un autre cas).
 *
 * On résout donc le chemin via ROUTES quand il correspond à une route connue,
 * et on ne retombe sur le préfixage naïf que pour les chemins dynamiques
 * (articles, hubs) dont l'appelant filtre déjà les langues disponibles.
 */
export function buildHreflang(pathname) {
  const clean = stripLangPrefix(pathname);
  const key = pathToKey(clean) ?? pathToKey(pathname);

  if (key) {
    const byLang = ROUTES[key];
    return [
      { lang: 'fr', href: `${SITE_URL}${byLang.fr}` },
      { lang: 'en', href: `${SITE_URL}${byLang.en}` },
      { lang: 'es', href: `${SITE_URL}${byLang.es}` },
      { lang: 'x-default', href: `${SITE_URL}${byLang.fr}` },
    ];
  }

  const tail = clean === '/' ? '' : clean;
  return [
    { lang: 'fr', href: `${SITE_URL}${tail || '/'}` },
    { lang: 'en', href: `${SITE_URL}/en${tail}` },
    { lang: 'es', href: `${SITE_URL}/es${tail}` },
    { lang: 'x-default', href: `${SITE_URL}${tail || '/'}` },
  ];
}

export function defaultMeta({ title, description, image, lang = 'fr', type = 'website' }) {
  return {
    title,
    description,
    image: image || DEFAULT_OG_IMAGE,
    ogLocale: OG_LOCALE_BY_LANG[lang] ?? OG_LOCALE_BY_LANG.fr,
    type,
  };
}

export { SITE_URL, DEFAULT_OG_IMAGE };
